import { useEffect, useState, useRef, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listGroups,
  searchContacts,
  createGroup,
  deleteGroup,
  resetContactGroup,
  uploadListChunked,
  previewImport,
  getImportStatus,
  discardGroup,
  type ContactSearchParams,
  type ContactGroup,
  type ContactGroupStatus,
  type ImportPreview,
  type TargetField,
} from "../api/contacts";
import { campaignActionErrorMessage } from "../api/campaigns";
import { enrichGroup, getEnrichStatus, getBreakdown } from "../api/lnp";

const FIELD_LABEL: Record<TargetField, string> = {
  phone: "Phone",
  mobile: "Mobile",
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  fax: "Fax",
  // Keep the column as a per-lead custom field: stored on the contact and shown
  // to the agent as a screen-pop during the call (under this column's header).
  custom: "Keep as custom field",
};
const IGNORE = "__ignore" as const;

const STATUS_BADGE: Record<ContactGroupStatus, { label: string; cls: string }> = {
  uploading: { label: "Uploading", cls: "bg-blue-100 text-blue-700" },
  processing: { label: "Scrubbing DNC", cls: "bg-amber-100 text-amber-700" },
  pending_approval: { label: "Needs approval", cls: "bg-purple-100 text-purple-700" },
  active: { label: "Active", cls: "bg-green-100 text-green-700" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700" },
  discarded: { label: "Discarded", cls: "bg-gray-100 text-gray-500" },
};

function StatusBadge({ status }: { status: ContactGroupStatus }) {
  const b = STATUS_BADGE[status] ?? STATUS_BADGE.active;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${b.cls}`}>{b.label}</span>;
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "good" | "warn" }) {
  const color = tone === "good" ? "text-green-700" : tone === "warn" ? "text-amber-700" : "text-gray-800";
  return (
    <div>
      <div className={`text-lg font-semibold ${color}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

/** Drag-and-drop chunked CSV uploader → background DNC scrub → approval review. */
function ListUploader() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, TargetField | typeof IGNORE>>({});
  const [progress, setProgress] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Poll the list's status while it's processing; stop once it settles.
  const { data: status } = useQuery({
    queryKey: ["import-status", groupId],
    queryFn: () => getImportStatus(groupId!),
    enabled: groupId !== null,
    refetchInterval: (q) => {
      const s = (q.state.data as ContactGroup | undefined)?.status;
      return s === "processing" || s === "uploading" ? 1500 : false;
    },
  });

  // Detect columns from a sample → seed the editable mapping with the suggestions.
  const previewMut = useMutation({
    mutationFn: (f: File) => previewImport(f),
    onSuccess: (pv) => {
      setPreview(pv);
      const init: Record<string, TargetField | typeof IGNORE> = {};
      for (const h of pv.headers) init[h] = pv.suggested[h] ?? IGNORE;
      setMapping(init);
    },
    onError: (e) => {
      setErr((e as Error).message);
      setFile(null);
    },
  });

  const upload = useMutation({
    mutationFn: () => {
      const clean: Record<string, TargetField> = {};
      for (const [h, f] of Object.entries(mapping)) if (f !== IGNORE) clean[h] = f;
      return uploadListChunked(name.trim(), file!, { mapping: clean, onProgress: setProgress });
    },
    onSuccess: (gid) => {
      setGroupId(gid);
      setProgress(null);
    },
    onError: (e) => setErr((e as Error).message),
  });

  const discard = useMutation({
    mutationFn: () => discardGroup(groupId!),
    onSuccess: () => {
      reset();
      qc.invalidateQueries({ queryKey: ["contact-groups"] });
    },
  });

  function reset() {
    setName("");
    setFile(null);
    setPreview(null);
    setMapping({});
    setGroupId(null);
    setProgress(null);
    setErr(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleFile(f: File | undefined) {
    setErr(null);
    if (!f) return;
    if (!name.trim()) {
      setErr("Name the list first.");
      return;
    }
    setFile(f);
    previewMut.mutate(f);
  }

  const busy =
    previewMut.isPending || upload.isPending ||
    status?.status === "uploading" || status?.status === "processing";

  // Step 2 — column mapping review (preview ready, upload not started yet).
  if (preview && groupId === null && progress === null && !upload.isPending) {
    const mapped = new Set(Object.values(mapping).filter((f) => f !== IGNORE));
    const hasDialable = mapped.has("phone") || mapped.has("mobile");
    return (
      <div className="bg-white rounded-lg shadow p-4 space-y-3 border-l-4 border-brand">
        <div className="text-sm font-semibold text-gray-700">Map the columns for “{name}”</div>
        <p className="text-xs text-gray-500">
          We detected these from your file — adjust any that look off. “Ignore” skips a column.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-3 py-2">Your column</th>
                <th className="px-3 py-2">Sample values</th>
                <th className="px-3 py-2">Maps to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {preview.headers.map((h, i) => (
                <tr key={`${h}-${i}`}>
                  <td className="px-3 py-2 font-medium">
                    {h || <span className="text-gray-400">(unnamed)</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {preview.sample_rows.map((r) => r[i]).filter(Boolean).slice(0, 3).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      value={mapping[h] ?? IGNORE}
                      onChange={(e) =>
                        setMapping((m) => ({ ...m, [h]: e.target.value as TargetField | typeof IGNORE }))
                      }
                    >
                      <option value={IGNORE}>Ignore</option>
                      {preview.target_fields.map((f) => (
                        <option key={f} value={f}>
                          {FIELD_LABEL[f]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!hasDialable && (
          <p className="text-xs text-amber-600">
            Tip: map a Phone or Mobile column so these contacts are dialable.
          </p>
        )}
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button
            onClick={() => upload.mutate()}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            Start import &amp; DNC scrub
          </button>
          <button
            onClick={reset}
            className="border border-gray-300 text-gray-700 rounded px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Done card — list finished processing + DNC scrub and auto-activated.
  // (Also handles legacy 'pending_approval' rows the same way.)
  if (status && (status.status === "active" || status.status === "pending_approval")) {
    const removed = status.dnc_removed_count;
    return (
      <div className="bg-white rounded-lg shadow p-4 space-y-3 border-l-4 border-green-400">
        <div className="text-sm font-semibold text-gray-700">
          ✓ “{status.name}” is ready &amp; live
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <Stat label="Imported" value={status.imported_count} />
          <Stat label="Removed (on DNC)" value={removed} tone={removed > 0 ? "warn" : undefined} />
          <Stat label="Skipped (blank)" value={status.skipped_count} />
          <Stat label="Ready to dial" value={status.contact_count} tone="good" />
        </div>
        <p className="text-xs text-gray-500">
          {removed.toLocaleString()} number{removed === 1 ? "" : "s"} on your or the global
          Do-Not-Call list {removed === 1 ? "was" : "were"} removed. It's now selectable when
          creating a campaign.
        </p>
        <div className="flex gap-2">
          <button
            onClick={reset}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            Upload another
          </button>
          <button
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
            className="border border-gray-300 text-gray-700 rounded px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Delete this list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <div className="text-sm font-semibold text-gray-700">Upload a contact list</div>
      <p className="text-xs text-gray-500">
        Name the list, then drop a CSV — any column layout. We auto-detect which column is
        the phone, name, email, etc. (and let you tweak it), scrub it against your &amp; the
        global DNC list, and let you approve it before it's dialable.
      </p>
      <input
        className="border border-gray-300 rounded px-3 py-2 text-sm w-full max-w-sm"
        placeholder="List name (e.g. “May Florida Buyers”)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={busy}
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !busy && fileRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-8 text-center text-sm transition-colors ${
          dragging ? "border-brand bg-brand/5" : "border-gray-300 hover:border-gray-400"
        } ${busy ? "opacity-60 pointer-events-none" : ""}`}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {progress !== null ? (
          <div className="space-y-1">
            <div className="text-gray-600">Uploading… {Math.round(progress * 100)}%</div>
            <div className="h-2 w-full max-w-sm mx-auto rounded bg-gray-200 overflow-hidden">
              <div className="h-full bg-brand transition-all" style={{ width: `${progress * 100}%` }} />
            </div>
          </div>
        ) : previewMut.isPending ? (
          <div className="text-gray-600">Detecting columns…</div>
        ) : status?.status === "processing" ? (
          <div className="text-amber-600">Parsing &amp; scrubbing against DNC…</div>
        ) : status?.status === "failed" ? (
          <div className="text-red-600">Processing failed. Try again.</div>
        ) : (
          <div className="text-gray-500">
            <span className="font-medium text-gray-700">Drop CSV here</span> or click to choose
          </div>
        )}
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

function BreakdownTable({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  return (
    <div className="border border-gray-200 rounded overflow-hidden">
      <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600">{title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <tr>
              <td className="px-3 py-2 text-gray-400">No data yet</td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.label}>
                <td className="px-3 py-1.5">{r.label}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{r.count.toLocaleString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** "Look up carriers" action + progress bar + carrier/line-type/status breakdown for a list. */
function LnpEnrichmentPanel({
  groupId,
  name,
  onClose,
}: {
  groupId: number;
  name: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<number | null>(null);

  const { data: breakdown, isLoading: breakdownLoading } = useQuery({
    queryKey: ["lnp-breakdown", groupId],
    queryFn: () => getBreakdown(groupId),
  });

  // Bound how long we'll poll a single job: a job stuck at 'queued' (worker
  // down / never picked up) or 'running' (worker hard-killed mid-job) would
  // otherwise poll every 2s forever with the button stuck disabled and no
  // feedback. pollStartRef is reset on every new job (even if the backend's
  // double-click dedup hands back the SAME job_id), so retrying after a
  // timeout gives the operator a fresh polling window.
  const POLL_TIMEOUT_MS = 10 * 60 * 1000; // ~10 minutes
  const pollStartRef = useRef<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const enrich = useMutation({
    mutationFn: () => enrichGroup(groupId),
    onSuccess: (d) => {
      pollStartRef.current = Date.now();
      setTimedOut(false);
      setJobId(d.job_id);
    },
  });

  // Terminal states the enrichment worker actually sets on lnp_enrichment_job
  // (see app/workers/lnp_enrich.py): 'done' on success, 'error' on failure.
  // Only polling on "done" left an errored job re-polling every 2s forever,
  // with the button stuck disabled/"Looking up…" and no feedback to the user.
  const isTerminal = (status: string | undefined) => status === "done" || status === "error";

  // Poll progress until the job reaches a terminal status or we give up.
  const { data: job } = useQuery({
    queryKey: ["lnp-enrich-status", jobId],
    queryFn: () => getEnrichStatus(jobId as number),
    enabled: jobId !== null,
    refetchInterval: (q) => (isTerminal(q.state.data?.status) || timedOut ? false : 2000),
  });

  // Recheck elapsed polling time on a coarse tick (independent of the 2s poll
  // itself) so we flip to "gave up" state and stop hammering the endpoint.
  useEffect(() => {
    if (jobId === null || timedOut || isTerminal(job?.status)) return;
    const t = setInterval(() => {
      if (pollStartRef.current && Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        setTimedOut(true);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [jobId, job?.status, timedOut]);

  // Once the job finishes, refresh the breakdown so the tables reflect the new data.
  useEffect(() => {
    if (job?.status === "done") {
      qc.invalidateQueries({ queryKey: ["lnp-breakdown", groupId] });
    }
  }, [job?.status, groupId, qc]);

  const stuck = timedOut && !isTerminal(job?.status);
  const running = jobId !== null && !isTerminal(job?.status) && !stuck;
  const errored = job?.status === "error";
  const pct = job && job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3 border-t-4 border-brand/30">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-800">Carrier lookup — {name}</h2>
        <button onClick={onClose} className="text-sm text-gray-500 hover:underline">
          Close
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          disabled={enrich.isPending || running}
          onClick={() => enrich.mutate()}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >
          {running ? "Looking up…" : "Look up carriers"}
        </button>
        {breakdown && (
          <span className="text-xs text-gray-500">
            {breakdown.enriched.toLocaleString()} enriched, {breakdown.un_enriched.toLocaleString()} not yet
            looked up
          </span>
        )}
      </div>
      {enrich.isError && <p className="text-sm text-red-600">{(enrich.error as Error).message}</p>}
      {errored && (
        <p className="text-sm text-red-600">
          Carrier lookup failed before finishing{job?.error ? `: ${job.error}` : ""}. Try "Look up
          carriers" again — numbers already looked up are still cached.
        </p>
      )}
      {stuck && (
        <p className="text-sm text-amber-600">
          Still working — this is taking longer than expected. Check back later; the lookup keeps
          running in the background even though we've stopped refreshing this page automatically.
        </p>
      )}

      {running && job && (
        <div className="space-y-1">
          <div className="h-2 w-full bg-gray-200 rounded overflow-hidden">
            <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-gray-500">
            {job.done.toLocaleString()} / {job.total.toLocaleString()} ({pct}%) — {job.status}
          </div>
        </div>
      )}

      {breakdownLoading ? (
        <p className="text-sm text-gray-500">Loading breakdown…</p>
      ) : breakdown ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BreakdownTable
            title="By carrier"
            rows={breakdown.by_carrier.map((c) => ({ label: c.carrier || c.spid || "Unknown", count: c.count }))}
          />
          <BreakdownTable
            title="By line type"
            rows={breakdown.by_line_type.map((l) => ({ label: l.line_type || "Unknown", count: l.count }))}
          />
          <BreakdownTable
            title="By status"
            rows={breakdown.by_status.map((s) => ({ label: s.status || "Unknown", count: s.count }))}
          />
        </div>
      ) : null}
    </div>
  );
}

function GroupsPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["contact-groups"],
    queryFn: () => listGroups(),
    // Reflect background scrub/approval transitions without a manual refresh.
    refetchInterval: (q) => {
      const groups = (q.state.data as ContactGroup[] | undefined) ?? [];
      return groups.some((g) => g.status === "processing" || g.status === "uploading") ? 2000 : false;
    },
  });
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [carrierGroup, setCarrierGroup] = useState<{ id: number; name: string } | null>(null);
  const [resetBanner, setResetBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const add = useMutation({
    mutationFn: () => createGroup({ name, description: desc || undefined }),
    onSuccess: () => {
      setName("");
      setDesc("");
      qc.invalidateQueries({ queryKey: ["contact-groups"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contact-groups"] }),
  });

  const resetGroup = useMutation({
    mutationFn: (id: number) => resetContactGroup(id),
    onSuccess: (res) => {
      if (res.campaigns_reset === 0) {
        setResetBanner({
          ok: true,
          text: `List "${res.group_name}" is not attached to any campaigns yet — attach it to a campaign to dial.`,
        });
      } else {
        const dncNote =
          res.dnc_suppressed > 0
            ? ` (${res.dnc_suppressed.toLocaleString()} newly DNC-suppressed)`
            : "";
        setResetBanner({
          ok: true,
          text: `Reset ${res.campaigns_reset} campaign(s) using "${res.group_name}": ${res.reset.toLocaleString()} contacts reset to pending, ${res.pending_total.toLocaleString()} ready to dial${dncNote}.`,
        });
      }
      qc.invalidateQueries({ queryKey: ["contact-groups"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (err) => {
      setResetBanner({ ok: false, text: campaignActionErrorMessage(err) });
    },
  });

  if (isLoading) return <p className="text-gray-500">Loading lists…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;

  const groups = data ?? [];
  return (
    <div className="space-y-4">
      <ListUploader />

      {resetBanner && (
        <div
          role={resetBanner.ok ? "status" : "alert"}
          className={`flex items-start justify-between gap-3 rounded border px-4 py-3 text-sm ${
            resetBanner.ok
              ? "border-green-300 bg-green-50 text-green-800"
              : "border-red-300 bg-red-50 text-red-700"
          }`}
        >
          <span>{resetBanner.text}</span>
          <button
            onClick={() => setResetBanner(null)}
            className="opacity-70 hover:opacity-100"
            aria-label="Dismiss reset message"
          >
            ×
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">List</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Contacts</th>
              <th className="px-4 py-2 text-right">Removed (DNC)</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groups.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-gray-500">
                  No lists yet — upload one above.
                </td>
              </tr>
            ) : (
              groups.map((g) => (
                <tr key={g.contact_group_id}>
                  <td className="px-4 py-2 font-medium">{g.name}</td>
                  <td className="px-4 py-2"><StatusBadge status={g.status} /></td>
                  <td className="px-4 py-2 text-right">{g.contact_count.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-gray-500">
                    {g.dnc_removed_count ? g.dnc_removed_count.toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                    <button
                      disabled={g.contact_count === 0}
                      onClick={() => setCarrierGroup({ id: g.contact_group_id, name: g.name })}
                      className="text-brand hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                      Carriers
                    </button>
                    <button
                      disabled={g.status !== "active" || resetGroup.isPending}
                      title="Reset dial progress on every campaign using this list (DNC numbers stay suppressed)"
                      onClick={() => {
                        if (
                          confirm(
                            `Reset list "${g.name}"? Dialed and failed contacts on campaigns using this list will be reset to pending while DNC numbers stay suppressed.`,
                          )
                        ) {
                          resetGroup.mutate(g.contact_group_id);
                        }
                      }}
                      className="text-brand hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                      Reset list
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete list "${g.name}"? Contacts are kept.`))
                          remove.mutate(g.contact_group_id);
                      }}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {carrierGroup && (
        <LnpEnrichmentPanel
          key={carrierGroup.id}
          groupId={carrierGroup.id}
          name={carrierGroup.name}
          onClose={() => setCarrierGroup(null)}
        />
      )}

      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">New empty list</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Optional"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </div>
        <button
          disabled={!name || add.isPending}
          onClick={() => add.mutate()}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >
          Add list
        </button>
      </div>
    </div>
  );
}

function SearchPanel() {
  const [params, setParams] = useState<ContactSearchParams>({ page: 1, per_page: 25 });
  const [submitted, setSubmitted] = useState<ContactSearchParams | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["contact-search", submitted],
    queryFn: () => searchContacts(submitted!),
    enabled: submitted !== null,
    placeholderData: keepPreviousData,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted({ ...params, page: 1 });
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-wrap gap-2 mb-3">
        <input
          placeholder="Phone"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          onChange={(e) => setParams((p) => ({ ...p, phone: e.target.value || undefined }))}
        />
        <input
          placeholder="Email"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          onChange={(e) => setParams((p) => ({ ...p, email: e.target.value || undefined }))}
        />
        <input
          placeholder="First name"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm"
          onChange={(e) => setParams((p) => ({ ...p, first_name: e.target.value || undefined }))}
        />
        <button
          type="submit"
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-1.5 text-sm"
        >
          Search
        </button>
      </form>
      {submitted && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Searching…" : `${data?.total ?? 0} match(es)`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).map((c) => (
                <tr key={c.contact_id}>
                  <td className="px-4 py-2">
                    {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="px-4 py-2">{c.phone || "—"}</td>
                  <td className="px-4 py-2">{c.email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ContactsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-4">Contact Lists</h1>
        <GroupsPanel />
      </div>
      <div>
        <h2 className="text-xl font-semibold mb-3">Search Contacts</h2>
        <SearchPanel />
      </div>
    </div>
  );
}
